/**
 * Licensed Materials - Property of HCL Technologies Limited.
 * (C) Copyright HCL Technologies Limited  2023.
 */

import { REGEX as REG_EX } from '@/data/constants/regex';
import { DEFAULT_COUNTRY_ID } from '@/data/Content/GeocodeByZipCode';
import {
	Address,
	AddressType,
	Attribute,
	EditableAddress,
	PrintableAddress,
} from '@/data/types/Address';
import { Country, State } from '@/data/types/CountryState';
import { BasicAddress } from '@/data/types/Order';
import { EditablePersonInfo, PersonContact } from '@/data/types/Person';
import {
	JavaUtilMapEntry,
	PersonPersonAttributes,
} from 'integration/generated/transactions/data-contracts';
import { DeepNonNullable } from '@/data/types/DeepNonNullable';
export { REG_EX };

export const ADDRESS_SHIPPING = 'Shipping';
export const ADDRESS_BILLING = 'Billing';
export const ADDRESS_SHIPPING_BILLING = 'ShippingAndBilling';

export const PrintableAttrs = [
	'addressLine1',
	'addressLine2',
	'addressLine3',
	'zipCode',
	'city',
	'country',
	'state',
];
export const ShippingTablePrintableAttrs = ['addressLine', 'physical', 'country'];

export const ADDRESS_INIT: EditableAddress = {
	firstName: '',
	lastName: '',
	city: '',
	state: '',
	zipCode: '',
	country: DEFAULT_COUNTRY_ID,
	phone1: '',
	nickName: '',
	email1: '',
	addressType: ADDRESS_SHIPPING_BILLING,
	addressLine1: '',
	addressLine2: '',
	addressLine3: '',
	addressField3: '',
};

export const EDITABLE_ADDRESS_INIT: DeepNonNullable<EditableAddress> = {
	firstName: '',
	lastName: '',
	city: '',
	state: '',
	zipCode: '',
	country: DEFAULT_COUNTRY_ID,
	phone1: '',
	nickName: '',
	email1: '',
	addressType: ADDRESS_SHIPPING_BILLING,
	addressLine1: '',
	addressLine2: '',
	addressLine3: '',
	addressField3: '',
	addressId: '',
	addressField1: '',
	addressLine3Txt: '',
	primary: 'false',
	isOrgAddress: false,
	field1: '',
	field3: '',
};

export const mapCountryStateOption = (options: State[] | Country[]) =>
	options.map((option) => option.displayName);

export const validateInput = (type: keyof typeof REG_EX, value: string) => {
	const stringType = REG_EX[type];
	return value !== undefined && value.trim() !== '' && stringType.test(value);
};

/**
 * @param _addr
 * @returns true if `_addr` has a non-empty country field and non-empty first address-line
 */
export const validateAddress = (
	_address:
		| (BasicAddress & { address1?: string })
		| (PersonContact & { address1?: string })
		| undefined
) => Boolean(_address?.country && (_address.addressLine?.at(0) || _address.address1));

export const makeEditable = (address: Address): EditableAddress => {
	const {
		addressLine,
		firstName = '',
		lastName = '',
		city = '',
		country = '',
		state = '',
		zipCode = '',
		phone1 = '',
		nickName = '',
		email1 = '',
		addressType = ADDRESS_SHIPPING_BILLING,
		addressId,
		primary,
		isOrgAddress,
		attributes,
	} = address || {};

	const field3Main =
		attributes?.find(
			(att: Attribute): att is PersonPersonAttributes =>
				'contactInfoAttrKey' in att && att['contactInfoAttrKey'] === 'addressField3'
		)?.contactInfoAttrValue ?? undefined;

	const field3 =
		attributes?.find(
			(att: Attribute): att is JavaUtilMapEntry => 'key' in att && att['key'] === 'addressField3'
		)?.value ?? undefined;

	return {
		firstName,
		lastName,
		city,
		country,
		state,
		zipCode,
		phone1,
		nickName,
		email1,
		addressType,
		addressId,
		primary,
		addressLine1: addressLine?.at(0) ?? '',
		addressLine2: addressLine?.at(1) ?? '',
		addressLine3: addressLine?.at(2) ?? '',
		addressField3: field3Main || field3,
		isOrgAddress,
	};
};

export const makePrintable = (address: BasicAddress | EditableAddress): PrintableAddress => {
	const { firstName, lastName, city, state, zipCode } = address;
	const fullName = [firstName, lastName].filter(Boolean).join(' ');
	const physical = [city, state, zipCode].filter(Boolean).join(', ');
	if (isEditableAddress(address)) {
		// editableAddress print to show validated address suggestion
		const { isOrgAddress, addressLine1, addressLine2, email1, phone1, ...addr } = address;
		return { ...addr, addressLine: [addressLine1, addressLine2], physical };
	} else {
		return { ...address, fullName, physical };
	}
};

type AddressTypeKey = 'Shipping' | 'Billing' | 'Both';

export const AddressTypes: Record<AddressTypeKey, AddressType> = {
	Shipping: ADDRESS_SHIPPING,
	Billing: ADDRESS_BILLING,
	Both: ADDRESS_SHIPPING_BILLING,
};

export const maskCC = (card: string) => card.replace(REG_EX.CARD_NUMBER_MASK, '*');

export const isEditableAddress = (
	address: EditableAddress | BasicAddress | PrintableAddress | Address
): address is EditableAddress =>
	(address as EditableAddress).addressLine1 !== undefined && !('addressLine' in address);

export const makeEditablePrincipalAddress = (address: EditablePersonInfo): EditableAddress => {
	const {
		addressLine1 = '',
		addressLine2 = '',
		addressLine3 = '',
		firstName = '',
		lastName = '',
		city = '',
		state = '',
		zipCode = '',
		phone1 = '',
		nickName = '',
		email1 = '',
		addressType = ADDRESS_SHIPPING_BILLING,
		addressId,
		primary,
		isOrgAddress,
		addressField3,
	} = address;

	return {
		firstName,
		lastName,
		city,
		country: DEFAULT_COUNTRY_ID,
		state,
		zipCode,
		phone1,
		nickName,
		email1,
		addressType,
		addressId,
		primary,
		addressLine1,
		addressLine2,
		addressLine3,
		addressField3,
		isOrgAddress,
	};
};
